import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { numericFormatter } from 'react-number-format';
import qs from 'qs';

import { Paper, Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Filter from 'components/Filter/components';
import FilterMobile from 'components/FilterMobile/components';
import Chip from 'components/Chip';
import Button from 'components/Button';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';

import StorageMovement from 'components/StorageMovement';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faSlidersH, faClockRotateLeft, faPen } from '@fortawesome/free-solid-svg-icons';
import { setProductSellUuid, clearProductSell } from '../../ProductsSellRegistration/store/actions';
import { loadProductsFilters, loadProductsList, loadProductsExport } from '../store/thunk';
import { loadBusinessInfo } from 'pages/BusinessInfo/store/thunk';
import { loadFilters } from 'components/Filter/store/thunk';

import { inputs } from './resources';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import useDidMount from 'helpers/hooks/useDidMount';

const headers = [
  { title: 'Nome', field: 'name' },
  { title: 'Categoria', field: 'productTypeName' },
  { title: 'Marca', field: 'brandName' },
  { title: 'Estoque', field: 'quantityToSellColumn' }
];

export default function ProductsSell() {
  const dispatch = useDispatch();

  const history = useHistory();

  const didComponentMount = useDidMount();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { establishments } = useSelector(state => state.login);
  const { qp } = useSelector(state => state.filters);
  const { measurementUnits } = useSelector(state => state.products.filters);

  const {
    isLoading,
    filters,
    list: {
      content,
      total
    }
  } = useSelector(state => state.products);

  const firstPremiumPackageEstablishment = establishments
    ?.filter(establishment => +establishment?.premiumPackage == 1)[0]
    ?.establishmentId;

  const productTypesParentsId = filters?.productTypes
    ?.map(product => product.parentProductTypeId)
    ?.filter((product, index, array) => array?.indexOf(product) == index);

  const productTypesParents = filters?.productTypes
    ?.filter(product => productTypesParentsId?.includes(product?.id));

  const formattedProductType = filters?.productTypes
    ?.map(type => {
      return {
        parent: productTypesParents?.find(product => +product?.id == +type?.parentProductTypeId)?.description,
        value: type?.id,
        label: type?.description
      }
    })
    ?.filter(type => !!type.parent);

  const formattedFilters = inputs
    ?.concat({
      advanced: false,
      type: 'autocomplete',
      id: 'brandId',
      label: 'Marca',
      value: '',
      options: [
        {
          label: '--',
          value: ''
        },
        ...!!filters?.productBrands && filters?.productBrands?.map(brand => {
          return {
            label: brand?.name,
            value: brand?.id
          }
        })
      ]
    })
    ?.concat({
      advanced: false,
      type: 'autocomplete',
      id: 'productTypeId',
      label: 'Categoria',
      value: '',
      options: [
        {
          label: '--',
          value: ''
        },
        ...formattedProductType
      ]
    });

  const [productsPage, setProductsPage] = useState(1);
  const productsPageSize = 10;
  const productsPageCount = Math.ceil(total / productsPageSize);

  const [isStorageMovementModalOpen, setIsStorageMovementModalOpen] = useState(false);
  const [isStorageMovementSell, setIsStorageMovementSell] = useState(false);
  const [isStorageMovementConsume, setIsStorageMovementConsume] = useState(false);
  const [storageMovementModalData, setStorageMovementModalData] = useState({});

  const handleLoadProductsExport = () => {
    const { visible } = qs.parse(qp) || {};

    const queryParams = {
      ...qs.parse(qp),
      visible: !!visible ? visible : 1,
      type: 'toSell'
    }

    return dispatch(loadProductsExport({ userId, establishmentId, qp: `?${qs.stringify(queryParams)}` }));
  }

  const requestProductsList = ({ establishmentId, page, qp }) => {
    const { visible } = qs.parse(qp) || {};

    const queryParams = {
      ...qs.parse(qp),
      visible: !!visible ? visible : 1,
      type: 'toSell',
      page
    }

    dispatch(loadProductsList({ userId, establishmentId, page, qp: `?${qs.stringify(queryParams)}` }));
  }

  useEffect(() => {
    if(!establishmentId || (+firstPremiumPackageEstablishment == +establishmentId)) {
      requestProductsList({ establishmentId: firstPremiumPackageEstablishment, page: 1, qp: '' });
      dispatch(loadProductsFilters(userId, firstPremiumPackageEstablishment));
      return;
    }

    if(+firstPremiumPackageEstablishment != +establishmentId) {
      requestProductsList({ establishmentId, page: 1, qp: '' });
      dispatch(loadProductsFilters(userId, establishmentId));
      return;
    }
  }, [firstPremiumPackageEstablishment]);

  useEffect(() => {
    if(establishmentId && !didComponentMount) {
      dispatch(loadBusinessInfo({ userId, establishmentId }));
      dispatch(loadFilters(userId, establishmentId));
      dispatch(loadProductsFilters(userId, establishmentId));
      requestProductsList({ establishmentId, page: productsPage, qp });
    }
  }, [establishmentId]);

  useEffect(() => {
    if(!didComponentMount) {
      requestProductsList({ establishmentId, page: productsPage, qp });
    }
  }, [productsPage]);

  const getQuantityStyle = (quantity, minQuantity) => {
    const quantityPercentage = +minQuantity * 100 / +quantity;

    if(quantityPercentage >= 80 || quantityPercentage < 0) {
      return {
        backgroundColor: 'rgba(246, 108, 107, 0.2)',
        color: '#F66C6B'
      }
    }

    if(quantityPercentage >= 50 && quantityPercentage < 80) {
      return {
        backgroundColor: 'rgba(245, 185, 65, 0.2)',
        color: '#F5B941'
      }
    }

    if(quantityPercentage < 50) {
      return {
        backgroundColor: 'rgba(19, 188, 75, 0.2)',
        color: '#43A047'
      }
    }

    if(!quantity || !minQuantity) {
      return {
        backgroundColor: 'rgba(19, 188, 75, 0.2)',
        color: '#43A047'
      }
    }
  }

  const formatQuantityToSell = ({ measurementUnitId, quantityToSell, minQuantityToSell }) => {
    const { abbreviation } = measurementUnits?.find(unit => unit?.id == measurementUnitId) || {};

    if(Number.isInteger(quantityToSell)) {
      return(
        <Tooltip
          placement='top'
          title={
            <Typography>
              Atual: {numericFormatter(quantityToSell ? quantityToSell?.toString() : '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })} {abbreviation}
              <br />
              Min: {numericFormatter(minQuantityToSell?.toString() || '', { thousandSeparator: '.', decimalSeparator: ',', decimalScale: 2, fixedDecimalScale: 2 })} {abbreviation}
            </Typography>
          }
          enterTouchDelay={0}
        >
          <Typography>
            <Chip
              label={parseInt(quantityToSell)}
              style={getQuantityStyle(quantityToSell, minQuantityToSell)}
            />
          </Typography>
        </Tooltip>
      );
    }

    return '';
  }

  return(
    <Paper>
      {isStorageMovementModalOpen && (
        <StorageMovement
          isStorageMovementModalOpen={isStorageMovementModalOpen}
          setIsStorageMovementModalOpen={setIsStorageMovementModalOpen}
          productName={storageMovementModalData?.name}
          storageProductUuid={storageMovementModalData?.storageProductUuid}
          isStorageMovementSell={isStorageMovementSell}
          isStorageMovementConsume={isStorageMovementConsume}
          pageOrigin='/produtos-venda'
          productListCurrentPage={productsPage}
        />
      )}
      {isMobile ? (
        <FilterMobile
          filter
          filters={formattedFilters}
          isDisabledPdfExport
          alternativeExport
          functionAlternativeExport={handleLoadProductsExport}
          exportType='productsSell'
          actionButton={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <Button
                type="button"
                color="success"
                startIcon={
                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    color="#FFFFFF"
                    size="xs"
                  />
                }
                onClick={() => {
                  dispatch(clearProductSell());
                  return history.push('/novo-produto-venda');
                }}
              >
                Adicionar Produto
              </Button>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 50%)', gap: 5 }}>
                <Button
                  color="primary"
                  onClick={() => history.push('/categorias-de-produtos')}
                >
                  Categorias
                </Button>
                <Button
                  color="primary"
                  onClick={() => history.push('/marcas-de-produtos')}
                >
                  Marcas
                </Button>
              </div>
            </div>
          }
          handlePageRequest={({ qp }) => {
            setProductsPage(1);
            return requestProductsList({ establishmentId, page: 1, qp });
          }}
        />
      ) : (
        <Filter
          filter
          filters={formattedFilters}
          isDisabledPdfExport
          alternativeExport
          functionAlternativeExport={handleLoadProductsExport}
          exportType='productsSell'
          actionButton={
            <div style={{ display: 'flex', gap: 5 }}>
              <Button
                type="button"
                color="success"
                startIcon={
                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    color="#FFFFFF"
                    size="xs"
                  />
                }
                onClick={() => {
                  dispatch(clearProductSell());
                  return history.push('/novo-produto-venda');
                }}
              >
                Adicionar Produto
              </Button>
              <Button
                color="primary"
                onClick={() => history.push('/categorias-de-produtos')}
              >
                Categorias
              </Button>
              <Button
                color="primary"
                onClick={() => history.push('/marcas-de-produtos')}
              >
                Marcas
              </Button>
            </div>
          }
          handlePageRequest={({ qp }) => {
            setProductsPage(1);
            return requestProductsList({ establishmentId, page: 1, qp });
          }}
        />
      )}
      {isMobile && (
        <TableMobile
          loading={isLoading}
          data={content?.map(item => {
            return {
              ...item,
              nameColumn: {
                index: 1,
                label: 'Nome',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.name}</Typography>
              },
              productTypeNameColumn: {
                index: 2,
                label: 'Categoria',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.productTypeName}</Typography>
              },
              brandNameColumn: {
                index: 3,
                label: 'Marca',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.brandName}</Typography>
              },
              quantityToSellColumn: {
                index: 4,
                label: 'Estoque',
                value: formatQuantityToSell(item)
              }
            }
          })}
          actions={[
            {
              action: rowData => (
                <Tooltip placement='top' title='Movimentar Estoque' enterTouchDelay={0}>
                  <IconButton onClick={() => {
                    setIsStorageMovementSell(true);
                    setIsStorageMovementConsume(false);
                    setStorageMovementModalData(rowData);
                    setIsStorageMovementModalOpen(true);
                  }}>
                    <FontAwesomeIcon
                      icon={faSlidersH}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            },
            {
              action: rowData => (
                <Tooltip placement='top' title='Histórico de Movimentação de Estoque' enterTouchDelay={0}>
                  <IconButton onClick={() => {
                    delete rowData['nameColumn'];
                    delete rowData['productTypeNameColumn'];
                    delete rowData['brandNameColumn'];
                    delete rowData['quantityToUseColumn'];
                    delete rowData['quantityToSellColumn'];

                    return history.push({
                      pathname: '/movimentacao-de-estoque',
                      state: {
                        data: {
                          product: rowData,
                          isConsumeAndIsSell: false,
                          isSell: true,
                          isConsume: false
                        }
                      }
                    });
                  }}>
                    <FontAwesomeIcon
                      icon={faClockRotateLeft}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            },
            {
              action: rowData => (
                <Tooltip placement='top' title='Editar Produto' enterTouchDelay={0}>
                  <IconButton onClick={() => {
                    dispatch(clearProductSell());
                    dispatch(setProductSellUuid(rowData?.storageProductUuid));
                    return history.push('/editar-produto-venda');
                  }}>
                    <FontAwesomeIcon
                      icon={faPen}
                      color="#022A5C"
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            }
          ]}
        />
      )}
      {!isMobile && (
        <MTable
          loading={isLoading}
          headers={headers}
          data={content?.map(item => {
            return {
              ...item,
              quantityToSellColumn: formatQuantityToSell(item)
            }
          })}
          actions={[
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faSlidersH}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: 'Movimentar Estoque',
              onClick: (_, rowData) => {
                setIsStorageMovementSell(true);
                setIsStorageMovementConsume(false);
                setStorageMovementModalData(rowData);
                setIsStorageMovementModalOpen(true);
              }
            }),
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faClockRotateLeft}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: 'Histórico de Movimentação de Estoque',
              onClick: (_, rowData) => {
                delete rowData['quantityToUseColumn'];
                delete rowData['quantityToSellColumn'];

                return history.push({
                  pathname: '/movimentacao-de-estoque',
                  state: {
                    data: {
                      product: rowData,
                      isConsumeAndIsSell: false,
                      isSell: true,
                      isConsume: false
                    }
                  }
                });
              }
            }),
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faPen}
                  color="#022A5C"
                  size="xs"
                />
              ),
              tooltip: 'Editar Produto',
              onClick: (_, rowData) => {
                dispatch(clearProductSell());
                dispatch(setProductSellUuid(rowData?.storageProductUuid));
                return history.push('/editar-produto-venda');
              }
            })
          ]}
        />
      )}
      <Grid
        container
        xs={12}
        style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
      >
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          count={productsPageCount}
          page={productsPage}
          onChange={(_, page) => setProductsPage(page)}
        />
      </Grid>
    </Paper>
  );
}